
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Shield, Lock, Bell, Timer, Key, Activity } from 'lucide-react';
import { useSecurity } from "@/hooks/useSecurity";
import { useSimpleProfile } from "@/hooks/useSimpleProfile";
import { useOptimizedAuth } from '@/hooks/useOptimizedAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import LoginActivity from "./security/LoginActivity";
import PasswordManagement from "./security/PasswordManagement";
import TwoFactorAuth from "./security/TwoFactorAuth";

const SecuritySettings = () => {
  const { user } = useOptimizedAuth();
  const { profile, updateProfile } = useSimpleProfile();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  
  const {
    passwordHistory,
    loginAttempts,
    twoFactorMethods,
    isLoadingHistory,
    isLoadingAttempts,
    changePassword,
    isChangingPassword,
    toggleTwoFactor,
    isTogglingTwoFactor,
  } = useSecurity();

  // Get current security settings
  const loginNotifications = profile?.notification_preferences?.login_notifications ?? true;
  const twoFactorEnabled = profile?.two_factor_enabled ?? false;

  const [settings, setSettings] = useState({
    loginNotifications,
    twoFactorEnabled,
  });

  const handleSettingChange = async (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    
    try {
      setIsLoading(true);
      
      if (key === 'loginNotifications') {
        await updateProfile({
          notification_preferences: {
            ...(profile?.notification_preferences || {}),
            login_notifications: value
          }
        });
      }
      
      if (key === 'twoFactorEnabled') {
        await updateProfile({
          two_factor_enabled: value
        });
      }

      toast({
        title: "Settings Updated",
        description: "Your security settings have been saved.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!user?.email) return;

    try {
      setIsLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/auth?mode=reset-password`,
      });

      if (error) throw error;

      toast({
        title: "Password reset sent",
        description: "Check your email for password reset instructions.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send password reset email",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Transform the data to match expected interfaces
  const transformedTwoFactorMethods = twoFactorMethods.map(method => ({
    ...method,
    last_used: method.last_used || ''
  }));

  const transformedLoginAttempts = loginAttempts.map(attempt => ({
    ...attempt,
    failure_reason: attempt.failure_reason || ''
  }));

  return (
    <div className="space-y-6">
      {/* Login Security Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Login Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>Login Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Get notified when someone logs into your account
              </p>
            </div>
            <Switch
              checked={settings.loginNotifications}
              onCheckedChange={(checked) => handleSettingChange('loginNotifications', checked)}
              disabled={isLoading}
            />
          </div>
        </CardContent>
      </Card>

      {/* Password Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Password Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Change Password</Label>
            <p className="text-sm text-muted-foreground">
              Send a password reset link to your email address
            </p>
            <Button
              onClick={handlePasswordReset}
              disabled={isLoading}
              variant="outline"
            >
              {isLoading ? "Sending..." : "Send Password Reset Email"}
            </Button>
          </div>
          
          <Separator />
          
          <PasswordManagement
            passwordHistory={passwordHistory}
            isLoadingHistory={isLoadingHistory}
            changePassword={changePassword}
            isChangingPassword={isChangingPassword}
          />
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <TwoFactorAuth
        profile={profile}
        twoFactorMethods={transformedTwoFactorMethods}
        toggleTwoFactor={toggleTwoFactor}
        isTogglingTwoFactor={isTogglingTwoFactor}
      />

      {/* Account Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Account Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm">Account Status</span>
              <span className="text-sm font-medium text-green-600">
                {profile?.account_status || 'Active'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Email Verified</span>
              <span className="text-sm font-medium text-green-600">
                {user?.email_confirmed_at ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Last Password Change</span>
              <span className="text-sm text-muted-foreground">
                {profile?.last_password_change ? 
                  new Date(profile.last_password_change).toLocaleDateString() : 
                  'Not available'
                }
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Login Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Login Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <LoginActivity
            loginAttempts={transformedLoginAttempts}
            isLoadingAttempts={isLoadingAttempts}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default SecuritySettings;
